import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, Globe, MapPin, CreditCard, Plane, History, Contact, Users, 
  Briefcase, GraduationCap, Shield, CheckCircle2, ArrowLeft, ArrowRight,
  HelpCircle, X, Check, Clock, AlertCircle, ChevronRight, Minus, 
  FileText, Sparkles, Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import DashboardLayout from "../components/DashboardLayout";

// Secciones del DS-160
const SECTIONS = [
  { id: 1, name: "Información Personal", icon: User, questions: 7, description: "Nombre, fecha de nacimiento, género" },
  { id: 2, name: "Nacionalidad", icon: Globe, questions: 5, description: "País de origen y ciudadanía" },
  { id: 3, name: "Dirección y Contacto", icon: MapPin, questions: 8, description: "Domicilio actual y teléfonos" },
  { id: 4, name: "Pasaporte", icon: CreditCard, questions: 6, description: "Datos del documento de viaje" },
  { id: 5, name: "Viaje", icon: Plane, questions: 5, description: "Propósito y fechas del viaje" },
  { id: 6, name: "Viajes Previos a EE.UU.", icon: History, questions: 4, description: "Historial de viajes anteriores" },
  { id: 7, name: "Contacto en EE.UU.", icon: Contact, questions: 6, description: "Persona o lugar de contacto" },
  { id: 8, name: "Familia", icon: Users, questions: 8, description: "Información de padres y cónyuge" },
  { id: 9, name: "Trabajo/Empleo", icon: Briefcase, questions: 7, description: "Empleo actual e ingresos" },
  { id: 10, name: "Educación", icon: GraduationCap, questions: 4, description: "Formación académica" },
  { id: 11, name: "Seguridad", icon: Shield, questions: 10, description: "Preguntas de elegibilidad" },
  { id: 12, name: "Confirmación", icon: CheckCircle2, questions: 2, description: "Revisar y confirmar" },
];

// Preguntas de ejemplo para la sección 7 (Contacto en EE.UU.)
const SECTION_7_QUESTIONS = [
  {
    id: "us_contact_name",
    label: "Nombre de la persona u organización de contacto en EE.UU.",
    type: "text",
    placeholder: "Ej: John Smith / Holiday Inn Miami",
    required: true,
    allowNA: true,
    naText: "No conozco a nadie en EE.UU.",
    hint: "Puede ser un familiar, amigo, hotel o empresa"
  },
  {
    id: "us_contact_relationship",
    label: "Relación con el contacto",
    type: "select",
    options: ["Familiar", "Amigo", "Conocido de negocios", "Hotel", "Empresa", "Otro"],
    required: true,
    allowNA: true,
    naText: "No aplica"
  },
  {
    id: "us_contact_address",
    label: "Dirección del contacto en EE.UU.",
    type: "text",
    placeholder: "123 Main Street",
    required: true,
    allowNA: true,
    naText: "No tengo la dirección exacta",
    hint: "Dirección completa incluyendo número"
  },
  {
    id: "us_contact_city",
    label: "Ciudad",
    type: "text",
    placeholder: "Miami",
    required: true,
    allowNA: false
  },
  {
    id: "us_contact_state",
    label: "Estado",
    type: "select",
    options: ["Florida", "California", "New York", "Texas", "Otro"],
    required: true,
    allowNA: false
  },
  {
    id: "us_contact_phone",
    label: "Teléfono del contacto",
    type: "text",
    placeholder: "+1 (305) 555-1234",
    required: false,
    allowNA: true,
    naText: "No tengo el número de teléfono"
  },
];

// Estado de demo para las secciones
const DEMO_SECTION_STATUS = {
  1: { completed: 7, status: "completed" },
  2: { completed: 5, status: "completed" },
  3: { completed: 6, status: "in_progress" },
  4: { completed: 0, status: "pending" },
  5: { completed: 0, status: "pending" },
  6: { completed: 0, status: "pending" },
  7: { completed: 0, status: "pending" },
  8: { completed: 0, status: "pending" },
  9: { completed: 0, status: "pending" },
  10: { completed: 0, status: "pending" },
  11: { completed: 0, status: "pending" },
  12: { completed: 0, status: "pending" },
};

// Componente de Tarjeta de Sección
const SectionCard = ({ section, status, onClick }) => {
  const Icon = section.icon;
  const progress = (status.completed / section.questions) * 100;
  
  const statusColors = {
    completed: "border-emerald-300 bg-emerald-50",
    in_progress: "border-amber-300 bg-amber-50",
    pending: "border-navy-200 bg-white"
  };
  
  const statusIcons = {
    completed: <CheckCircle2 className="w-5 h-5 text-emerald-500" />,
    in_progress: <Clock className="w-5 h-5 text-amber-500" />,
    pending: <AlertCircle className="w-5 h-5 text-navy-300" />
  };
  
  const statusLabels = {
    completed: "Completada",
    in_progress: "En progreso",
    pending: "Pendiente"
  };

  return (
    <motion.button
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={onClick}
      className={`w-full p-4 rounded-2xl border-2 ${statusColors[status.status]} text-left transition-all hover:shadow-lg`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          status.status === "completed" ? "bg-emerald-500 text-white" :
          status.status === "in_progress" ? "bg-amber-500 text-white" :
          "bg-navy-100 text-navy-500"
        }`}>
          <Icon className="w-6 h-6" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-navy-900 truncate">{section.name}</h3>
            {statusIcons[status.status]}
          </div>
          <p className="text-sm text-navy-500 mb-3">{section.description}</p>
          
          {/* Barra de progreso */}
          <div className="flex items-center gap-3">
            <div className="flex-1 h-2 bg-navy-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className={`h-full rounded-full ${
                  status.status === "completed" ? "bg-emerald-500" :
                  status.status === "in_progress" ? "bg-amber-500" :
                  "bg-navy-300"
                }`}
              />
            </div>
            <span className="text-xs font-medium text-navy-600 whitespace-nowrap">
              {status.completed}/{section.questions}
            </span>
          </div>
          
          <div className="flex items-center justify-between mt-3">
            <span className={`text-xs font-medium px-2 py-1 rounded-full ${
              status.status === "completed" ? "bg-emerald-100 text-emerald-700" :
              status.status === "in_progress" ? "bg-amber-100 text-amber-700" :
              "bg-navy-100 text-navy-600"
            }`}>
              {statusLabels[status.status]}
            </span>
            <ChevronRight className="w-5 h-5 text-navy-400" />
          </div>
        </div>
      </div>
    </motion.button>
  );
};

// Componente de Pregunta Individual
const QuestionItem = ({ question, value, naValue, onAnswer, onNA, questionNumber, totalQuestions }) => {
  const [showHelp, setShowHelp] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-navy-200 p-6 shadow-sm"
    >
      {/* Header de pregunta */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-medium text-navy-400">
          Pregunta {questionNumber} de {totalQuestions}
        </span>
        {question.allowNA && (
          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded-full">
            Permite "No Aplica"
          </span>
        )}
      </div>
      
      {/* Label de la pregunta */}
      <div className="flex items-start gap-2 mb-4">
        <label className="text-lg font-semibold text-navy-900 flex-1">
          {question.label}
          {question.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {question.hint && (
          <button
            onClick={() => setShowHelp(!showHelp)}
            className="p-1.5 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
          >
            <HelpCircle className="w-4 h-4" />
          </button>
        )}
      </div>
      
      {/* Tooltip de ayuda */}
      <AnimatePresence>
        {showHelp && question.hint && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-xl"
          >
            <div className="flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-blue-500 mt-0.5" />
              <p className="text-sm text-blue-700">{question.hint}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Campo de respuesta - deshabilitado si marcó N/A */}
      <div className={`transition-opacity ${naValue ? "opacity-50" : ""}`}>
        {question.type === "text" ? (
          <Input
            value={naValue ? "No Aplica" : value || ""}
            onChange={(e) => onAnswer(e.target.value)}
            placeholder={question.placeholder}
            disabled={naValue}
            className="text-lg py-6"
          />
        ) : question.type === "select" ? (
          <Select value={naValue ? "na" : value || ""} onValueChange={onAnswer} disabled={naValue}>
            <SelectTrigger className="text-lg py-6">
              <SelectValue placeholder="Seleccionar opción..." />
            </SelectTrigger>
            <SelectContent>
              {question.options.map(opt => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>
      
      {/* Botón "No Aplica" */}
      {question.allowNA && (
        <div className="mt-4 pt-4 border-t border-navy-100">
          <button
            onClick={() => onNA(!naValue)}
            className={`w-full flex items-center justify-center gap-3 p-4 rounded-xl border-2 transition-all ${
              naValue 
                ? "border-amber-400 bg-amber-50 text-amber-700" 
                : "border-navy-200 bg-navy-50 text-navy-600 hover:border-amber-300 hover:bg-amber-50"
            }`}
          >
            {naValue ? (
              <>
                <Check className="w-5 h-5" />
                <span className="font-medium">Marcado como: {question.naText}</span>
              </>
            ) : (
              <>
                <Minus className="w-5 h-5" />
                <span className="font-medium">{question.naText}</span>
              </>
            )}
          </button>
        </div>
      )}
    </motion.div>
  );
};

// Vista Interior de una Sección
const SectionDetailView = ({ section, onBack, onComplete }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [naAnswers, setNaAnswers] = useState({});
  
  const questions = SECTION_7_QUESTIONS; // Usando las preguntas de ejemplo
  const totalAnswered = Object.keys(answers).length + Object.keys(naAnswers).length;
  const progress = (totalAnswered / questions.length) * 100;
  const isComplete = totalAnswered === questions.length;
  
  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    // Remover de N/A si responde
    setNaAnswers(prev => {
      const newNa = { ...prev };
      delete newNa[questionId];
      return newNa;
    });
  };
  
  const handleNA = (questionId, isNA) => {
    if (isNA) {
      setNaAnswers(prev => ({ ...prev, [questionId]: true }));
      // Remover respuesta normal
      setAnswers(prev => {
        const newAns = { ...prev };
        delete newAns[questionId];
        return newAns;
      });
    } else {
      setNaAnswers(prev => {
        const newNa = { ...prev };
        delete newNa[questionId];
        return newNa;
      });
    }
  };
  
  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };
  
  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-navy-600 hover:text-navy-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a secciones
        </button>
        
        <div className="flex items-center gap-4">
          <div className={`w-14 h-14 rounded-xl flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-white`}>
            <section.icon className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-navy-900">{section.name}</h1>
            <p className="text-navy-500">{section.description}</p>
          </div>
        </div>
      </div>
      
      {/* Progreso de la sección */}
      <div className="bg-gradient-to-r from-navy-800 to-navy-900 rounded-2xl p-5 mb-6 text-white">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-navy-300">Progreso de esta sección</span>
          <span className="text-2xl font-bold">{Math.round(progress)}%</span>
        </div>
        <div className="h-3 bg-navy-700 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            className={`h-full rounded-full ${progress === 100 ? "bg-emerald-500" : "bg-amber-400"}`}
          />
        </div>
        <p className="text-sm text-navy-300 mt-2">
          {totalAnswered} de {questions.length} preguntas respondidas
        </p>
      </div>
      
      {/* Navegación de preguntas (dots) */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {questions.map((q, index) => {
          const isAnswered = answers[q.id] || naAnswers[q.id];
          const isCurrent = index === currentQuestion;
          return (
            <button
              key={q.id}
              onClick={() => setCurrentQuestion(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                isCurrent 
                  ? "w-8 bg-blue-500" 
                  : isAnswered 
                  ? "bg-emerald-500" 
                  : "bg-navy-200"
              }`}
            />
          );
        })}
      </div>
      
      {/* Pregunta actual */}
      <QuestionItem
        question={questions[currentQuestion]}
        value={answers[questions[currentQuestion].id]}
        naValue={naAnswers[questions[currentQuestion].id]}
        onAnswer={(value) => handleAnswer(questions[currentQuestion].id, value)}
        onNA={(isNA) => handleNA(questions[currentQuestion].id, isNA)}
        questionNumber={currentQuestion + 1}
        totalQuestions={questions.length}
      />
      
      {/* Navegación entre preguntas */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="outline"
          onClick={prevQuestion}
          disabled={currentQuestion === 0}
          className="px-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Anterior
        </Button>
        
        {currentQuestion < questions.length - 1 ? (
          <Button
            onClick={nextQuestion}
            className="bg-blue-600 hover:bg-blue-700 px-6"
          >
            Siguiente
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={onComplete}
            disabled={!isComplete}
            className={`px-8 ${isComplete ? "bg-emerald-600 hover:bg-emerald-700" : "bg-navy-300"}`}
          >
            <Check className="w-4 h-4 mr-2" />
            Terminé esta sección
          </Button>
        )}
      </div>
      
      {/* Mensaje de sección incompleta */}
      {!isComplete && currentQuestion === questions.length - 1 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl"
        >
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            <p className="text-sm text-amber-700">
              Faltan <strong>{questions.length - totalAnswered} pregunta(s)</strong> por responder. 
              Usa "No Aplica" si alguna pregunta no te corresponde.
            </p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Componente Principal
const DS160MockupDemo = () => {
  const [view, setView] = useState("overview"); // "overview" | "detail"
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionStatus, setSectionStatus] = useState(DEMO_SECTION_STATUS);
  
  const totalSections = SECTIONS.length;
  const completedSections = Object.values(sectionStatus).filter(s => s.status === "completed").length;
  const totalQuestions = SECTIONS.reduce((acc, s) => acc + s.questions, 0);
  const answeredQuestions = SECTIONS.reduce((acc, s) => acc + (sectionStatus[s.id]?.completed || 0), 0);
  const overallProgress = Math.round((answeredQuestions / totalQuestions) * 100);
  
  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setView("detail");
  };
  
  const handleBack = () => {
    setView("overview");
    setSelectedSection(null);
  };
  
  const handleComplete = () => {
    if (selectedSection) {
      setSectionStatus(prev => ({
        ...prev,
        [selectedSection.id]: {
          completed: selectedSection.questions,
          status: "completed"
        }
      }));
    }
    setView("overview");
    setSelectedSection(null);
  };

  return (
    <DashboardLayout>
      {/* Banner de Demo */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-2xl mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <Play className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-bold">MOCKUP - Vista Previa del Nuevo DS-160</h2>
            <p className="text-sm text-purple-200">Esta es una demostración interactiva de la nueva interfaz propuesta</p>
          </div>
        </div>
      </div>

      {view === "overview" ? (
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-navy-900 mb-2">
              Formulario DS-160
            </h1>
            <p className="text-navy-500">
              Completa cada sección en el orden que prefieras. Puedes pausar y continuar cuando quieras.
            </p>
          </div>
          
          {/* Progreso Global */}
          <div className="bg-gradient-to-r from-navy-800 to-navy-900 rounded-2xl p-6 mb-8 text-white">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold">Progreso Total</h3>
                <p className="text-navy-300 text-sm">Formulario DS-160 - Solicitud de Visa</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-bold">{overallProgress}%</div>
                <p className="text-navy-300 text-sm">{answeredQuestions}/{totalQuestions} preguntas</p>
              </div>
            </div>
            
            <div className="h-4 bg-navy-700 rounded-full overflow-hidden mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${overallProgress}%` }}
                transition={{ duration: 0.8 }}
                className={`h-full rounded-full ${
                  overallProgress === 100 ? "bg-emerald-500" : 
                  overallProgress >= 50 ? "bg-amber-400" : "bg-amber-500"
                }`}
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-emerald-400">{completedSections}</div>
                <p className="text-xs text-navy-300">Secciones completas</p>
              </div>
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-amber-400">
                  {Object.values(sectionStatus).filter(s => s.status === "in_progress").length}
                </div>
                <p className="text-xs text-navy-300">En progreso</p>
              </div>
              <div className="bg-navy-700/50 rounded-xl p-3 text-center">
                <div className="text-2xl font-bold text-navy-400">
                  {totalSections - completedSections - Object.values(sectionStatus).filter(s => s.status === "in_progress").length}
                </div>
                <p className="text-xs text-navy-300">Pendientes</p>
              </div>
            </div>
          </div>
          
          {/* Grid de Secciones */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {SECTIONS.map(section => (
              <SectionCard
                key={section.id}
                section={section}
                status={sectionStatus[section.id]}
                onClick={() => handleSectionClick(section)}
              />
            ))}
          </div>
          
          {/* Footer con información */}
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-900">¿Cómo funciona?</h4>
                <ul className="mt-2 space-y-1 text-sm text-blue-700">
                  <li>• Haz clic en cualquier sección para comenzar a responder</li>
                  <li>• Usa <strong>"No Aplica"</strong> si una pregunta no te corresponde</li>
                  <li>• Marca <strong>"Terminé esta sección"</strong> cuando completes todas las preguntas</li>
                  <li>• Puedes regresar a cualquier sección para modificar tus respuestas</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <SectionDetailView
          section={selectedSection}
          onBack={handleBack}
          onComplete={handleComplete}
        />
      )}
    </DashboardLayout>
  );
};

export default DS160MockupDemo;
